import { createSlice } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import { ApiStatus } from "../../utils/api.types";
import { ICoaSliceState } from "../../utils/coa.types";
import {
    fetchGroupCategories,
    createGroupCategory,
    updateGroupCategory,
    deleteGroupCategory,
    fetchPrimaryGroups,
    createPrimaryGroup,
    updatePrimaryGroup,
    deletePrimaryGroup,
    fetchSubGroups,
    createSubGroup,
    updateSubGroup,
    deleteSubGroup,
    fetchLedgers,
    createLedger,
    updateLedger,
    deleteLedger
} from "./coaThunk";

const initialState: ICoaSliceState = {
    groupCategories: [],
    groupCategoriesTotal: 0,
    primaryGroups: [],
    primaryGroupsTotal: 0,
    subGroups: [],
    subGroupsTotal: 0,
    ledgers: [],
    ledgersTotal: 0,
    status: ApiStatus.IDLE,
    errorMessage: ''
};

const coaSlice = createSlice({
    name: 'coa',
    initialState,
    reducers: {
        clearCoaState: (state) => {
            state.groupCategories = [];
            state.primaryGroups = [];
            state.subGroups = [];
            state.ledgers = [];
            state.status = ApiStatus.IDLE;
            state.errorMessage = '';
        }
    },
    extraReducers: (builder) => {
        builder
            // ==================== Group Categories ====================
            .addCase(fetchGroupCategories.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchGroupCategories.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.groupCategories = action.payload.data || [];
                state.groupCategoriesTotal = action.payload.total || 0;
                state.errorMessage = '';
            })
            .addCase(fetchGroupCategories.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message;
            })
            .addCase(createGroupCategory.fulfilled, (state, action) => {
                toast.success('Group Category created successfully');
            })
            .addCase(updateGroupCategory.fulfilled, (state, action) => {
                toast.success('Group Category updated successfully');
            })
            .addCase(deleteGroupCategory.fulfilled, (state, action) => {
                state.groupCategories = state.groupCategories.filter(item => item._id !== action.payload);
                toast.success('Group Category deleted successfully');
            })

            // ==================== Primary Groups ====================
            .addCase(fetchPrimaryGroups.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchPrimaryGroups.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.primaryGroups = action.payload.data || [];
                state.primaryGroupsTotal = action.payload.total || 0;
                state.errorMessage = '';
            })
            .addCase(fetchPrimaryGroups.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message;
            })
            .addCase(createPrimaryGroup.fulfilled, (state, action) => {
                toast.success('Primary Group created successfully');
            })
            .addCase(updatePrimaryGroup.fulfilled, (state, action) => {
                toast.success('Primary Group updated successfully');
            })
            .addCase(deletePrimaryGroup.fulfilled, (state, action) => {
                state.primaryGroups = state.primaryGroups.filter(item => item._id !== action.payload);
                toast.success('Primary Group deleted successfully');
            })

            // ==================== Sub Groups ====================
            .addCase(fetchSubGroups.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchSubGroups.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.subGroups = action.payload.data || [];
                state.subGroupsTotal = action.payload.total || 0;
                state.errorMessage = '';
            })
            .addCase(fetchSubGroups.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message;
            })
            .addCase(createSubGroup.fulfilled, (state, action) => {
                toast.success('Sub Group created successfully');
            })
            .addCase(updateSubGroup.fulfilled, (state, action) => {
                toast.success('Sub Group updated successfully');
            })
            .addCase(deleteSubGroup.fulfilled, (state, action) => {
                state.subGroups = state.subGroups.filter(item => item._id !== action.payload);
                toast.success('Sub Group deleted successfully');
            })

            // ==================== Ledgers ====================
            .addCase(fetchLedgers.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchLedgers.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.ledgers = action.payload.data || [];
                state.ledgersTotal = action.payload.total || 0;
                state.errorMessage = '';
            })
            .addCase(fetchLedgers.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message;
            })
            .addCase(createLedger.fulfilled, (state, action) => {
                toast.success('Ledger created successfully');
            })
            .addCase(updateLedger.fulfilled, (state, action) => {
                toast.success('Ledger updated successfully');
            })
            .addCase(deleteLedger.fulfilled, (state, action) => {
                state.ledgers = state.ledgers.filter(item => item._id !== action.payload);
                toast.success('Ledger deleted successfully');
            });
    }
});

export const { clearCoaState } = coaSlice.actions;
export default coaSlice.reducer;
